import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AttributeOption, PaginatedResponse, Product, QueryOptions } from '~shared/models';
import { SearchResponse } from '~app/modules/catalog/models';
import { QueryParamsEncoder } from '~shared/helpers/query-encoder';
import { isPlatformBrowser } from '@angular/common';
import { API_SEARCH_ATTRBIUTE_ROUTE_V3 } from '~app/modules/catalog/constants';
import { DEFAULT_LIMIT } from '~app/modules/generic/constants';
import { queryBuilder } from '@metromarkets/sdk';
import { Attribute } from '~app/modules/filters/models';
import {
  AttributeSearchOption,
  AttributeSearchPayload
} from '~app/modules/generic/models/attribute-search-payload.model';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';

@Injectable({
  providedIn: 'root'
})
export class SearchHttpService {

  constructor(
    private http: HttpClient,
    private queryParamsEncoder: QueryParamsEncoder,
    @Inject(PLATFORM_ID) private platformId: Object,
    private productsListUrlService: ProductsListUrlService,
    private priceTypeService: PriceTypeService) {
  }

  getPaginatedProducts(url: string, queryParams: HttpParams): Observable<PaginatedResponse<Product> | SearchResponse> {
    if (isPlatformBrowser(this.platformId)) {
      const params = new HttpParams({
        fromObject: {
          ...queryParams
        },
        encoder: this.queryParamsEncoder
      });

      return this.http.get<PaginatedResponse<Product> | SearchResponse>(url, {params});
    }
    return of({} as any);
  }

  attributesSearch(attributeCode: string, attributeOptionNameSearch: string = '', queryParams):
    Observable<Attribute[]> {
    const params: HttpParams = this.getAttributeSearchAPIHTTPParams(queryParams, attributeCode, attributeOptionNameSearch);

    return this.http.get<Attribute[]>(API_SEARCH_ATTRBIUTE_ROUTE_V3(), {params});
  }

  mapAttributesOptions(attributeOptions: AttributeOption[] = []): AttributeSearchPayload {
    return {
      grouppedAttributeOptions: attributeOptions.reduce((accumulator: AttributeSearchOption[], item: AttributeOption) => {
        const firstLetter: string = item.name.trim()[0].toUpperCase();
        const existingGroup = accumulator.find((group) => group.key === firstLetter);

        if (existingGroup) {
          existingGroup.options.push({
            ...item
          });
        } else {
          accumulator.push({
            key: firstLetter,
            options: [item],
          });
        }

        return accumulator;
      }, []).sort((attributeSearchOptionA: AttributeSearchOption, attributeSearchOptionB: AttributeSearchOption): number => {
        const keyA: string = attributeSearchOptionA.key.toLowerCase();
        const keyB: string = attributeSearchOptionB.key.toLowerCase();

        if (keyA < keyB) {
          return -1;
        }
        if (keyA > keyB) {
          return 1;
        }
        return 0;
      }),
      total: attributeOptions.length,
    };
  }

  private getAttributeSearchAPIHTTPParams(queryOptions: QueryOptions = {},
                                          attributeCode: string,
                                          attributeOptionNameSearch: string): HttpParams {
    const {...restQuery} = queryOptions;
    const offset: number = queryOptions?.offset || 0;
    const limit: number = queryOptions?.limit || DEFAULT_LIMIT;
    const q: string = queryOptions?.q;


    let topPhraseParam;

    if (this.productsListUrlService.isSearchPage()) {
      topPhraseParam = {
        'filter[top][phrase]': q
      };
    }

    let categoryParam;

    if (this.productsListUrlService.isCategoryPage()) {
      categoryParam = {
        'filter[categories]': [this.productsListUrlService.getCategory()]
      };
    }

    const params = {
      offset,
      limit,
      'filter[priceType]': this.priceTypeService.getPriceTypeSync(),
      'attribute[code]': attributeCode,
      'attribute[value]': attributeOptionNameSearch,
      ...topPhraseParam,
      ...categoryParam,
      ...restQuery,
      ...{useRecommendedProductsParser: '1'},
      ...{useCharacteristics: '1'}
    };

    return queryBuilder.toParams(params) as HttpParams;
  }
}


